/**
 * Account appearance — CraftPix avatars + username + colors + seat scale.
 * Saves to POST /api/user/settings (D1).
 */
import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import {
  AVATAR_PACKS,
  avatarUrl,
  clampAvatarScale,
  listAvatarIds,
  type AvatarPackId,
  type PlayerAppearance,
  DEFAULT_APPEARANCE,
} from "@shared/player-appearance";
import { Loader2, Save, UserCircle } from "lucide-react";

const PRESET_COLORS = [
  "#fbbf24",
  "#f472b6",
  "#60a5fa",
  "#4ade80",
  "#a78bfa",
  "#fb923c",
  "#ffffff",
  "#ef4444",
];

interface Props {
  wallet: string;
  initial?: Partial<PlayerAppearance> | null;
  onSaved?: (a: PlayerAppearance) => void;
}

export function AvatarAppearanceEditor({ wallet, initial, onSaved }: Props) {
  const { toast } = useToast();
  const [pack, setPack] = useState<AvatarPackId>("cute");
  const [avatarId, setAvatarId] = useState(
    initial?.avatarId || DEFAULT_APPEARANCE.avatarId,
  );
  const [username, setUsername] = useState(initial?.username || "");
  const [nameColor, setNameColor] = useState(
    initial?.nameColor || DEFAULT_APPEARANCE.nameColor,
  );
  const [frameColor, setFrameColor] = useState(
    initial?.frameColor || DEFAULT_APPEARANCE.frameColor,
  );
  const [avatarScale, setAvatarScale] = useState(
    initial?.avatarScale ?? DEFAULT_APPEARANCE.avatarScale,
  );
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(`/api/user/settings?wallet=${encodeURIComponent(wallet)}`, {
      headers: { "X-Wallet-Address": wallet },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        const app = (data.appearance || data) as PlayerAppearance;
        if (app.avatarId) {
          setAvatarId(app.avatarId);
          const p = app.avatarId.split("/")[0] as AvatarPackId;
          if (p === "cute" || p === "cartoon" || p === "funny") setPack(p);
        }
        if (app.username || data.displayName) {
          setUsername(String(app.username || data.displayName || ""));
        }
        if (app.nameColor) setNameColor(app.nameColor);
        if (app.frameColor) setFrameColor(app.frameColor);
        if (app.avatarScale != null) setAvatarScale(Number(app.avatarScale));
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [wallet]);

  const packAvatars = useMemo(() => listAvatarIds(pack), [pack]);

  const previewScale = clampAvatarScale(avatarScale);
  const previewUrl = avatarUrl(avatarId);

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch("/api/user/settings", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Wallet-Address": wallet,
        },
        body: JSON.stringify({
          wallet,
          displayName: username,
          appearance: {
            avatarId,
            username,
            nameColor,
            frameColor,
            avatarScale: previewScale,
          },
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Save failed");
      const appearance = (data.appearance || {
        avatarId,
        username: data.username || username,
        nameColor,
        frameColor,
        avatarScale: previewScale,
      }) as PlayerAppearance;
      toast({
        title: "Appearance saved",
        description: "Shown when you sit at tables",
      });
      onSaved?.(appearance);
    } catch (e) {
      toast({
        title: "Could not save",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-zinc-400 py-8 justify-center">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading appearance…
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start gap-6">
        {/* Live preview */}
        <div className="flex flex-col items-center gap-2 min-w-[100px]">
          <div
            style={{
              width: 72 * previewScale,
              height: 72 * previewScale,
              borderRadius: "50%",
              border: `3px solid ${frameColor}`,
              boxShadow: `0 0 16px ${frameColor}55`,
              overflow: "hidden",
              background: "rgba(0,0,0,0.4)",
            }}
          >
            <img
              src={previewUrl}
              alt="Your avatar"
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
              onError={(e) => {
                (e.target as HTMLImageElement).src = "/avatars/cute/01.png";
              }}
            />
          </div>
          <span
            className="text-sm font-semibold max-w-[100px] truncate"
            style={{ color: nameColor }}
          >
            {username || "Player"}
          </span>
          <Badge variant="outline" className="text-[10px] border-zinc-600">
            Table preview
          </Badge>
        </div>

        <div className="flex-1 space-y-3 min-w-[200px]">
          <div>
            <Label htmlFor="display-name" className="text-zinc-300">
              Display name
            </Label>
            <Input
              id="display-name"
              maxLength={16}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Name at the table"
              className="mt-1 bg-black/40 border-zinc-700"
            />
            <p className="text-[10px] text-zinc-500 mt-1">
              2–16 characters · letters, numbers, _ - .
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-zinc-300">Name color</Label>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {PRESET_COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    title={c}
                    onClick={() => setNameColor(c)}
                    className="w-7 h-7 rounded-full border-2"
                    style={{
                      background: c,
                      borderColor: nameColor === c ? "#fff" : "transparent",
                    }}
                  />
                ))}
                <input
                  type="color"
                  value={nameColor}
                  onChange={(e) => setNameColor(e.target.value)}
                  className="w-7 h-7 cursor-pointer bg-transparent"
                />
              </div>
            </div>
            <div>
              <Label className="text-zinc-300">Frame color</Label>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {PRESET_COLORS.map((c) => (
                  <button
                    key={`f-${c}`}
                    type="button"
                    title={c}
                    onClick={() => setFrameColor(c)}
                    className="w-7 h-7 rounded-full border-2"
                    style={{
                      background: c,
                      borderColor: frameColor === c ? "#fff" : "transparent",
                    }}
                  />
                ))}
                <input
                  type="color"
                  value={frameColor}
                  onChange={(e) => setFrameColor(e.target.value)}
                  className="w-7 h-7 cursor-pointer bg-transparent"
                />
              </div>
            </div>
          </div>

          <div>
            <Label className="text-zinc-300">
              Avatar size at table{" "}
              <span className="text-zinc-500">{previewScale.toFixed(2)}×</span>
            </Label>
            <input
              type="range"
              min={0.7}
              max={1.4}
              step={0.05}
              value={previewScale}
              onChange={(e) => setAvatarScale(Number(e.target.value))}
              className="w-full mt-2 accent-amber-500"
            />
          </div>
        </div>
      </div>

      {/* Pack tabs */}
      <div className="flex flex-wrap gap-2">
        {AVATAR_PACKS.map((p) => (
          <Button
            key={p.id}
            type="button"
            size="sm"
            variant={pack === p.id ? "default" : "outline"}
            className={
              pack === p.id
                ? "bg-amber-600 hover:bg-amber-500"
                : "border-zinc-700"
            }
            onClick={() => setPack(p.id)}
          >
            {p.name}
            <Badge className="ml-2 bg-black/30 text-[10px]">35</Badge>
          </Button>
        ))}
      </div>

      <div className="grid grid-cols-5 sm:grid-cols-7 md:grid-cols-9 gap-2 max-h-[280px] overflow-y-auto p-1 rounded-lg border border-zinc-800 bg-black/30">
        {packAvatars.map((id) => {
          const selected = id === avatarId;
          return (
            <button
              key={id}
              type="button"
              onClick={() => setAvatarId(id)}
              title={id}
              className="relative aspect-square rounded-lg overflow-hidden border-2 transition"
              style={{
                borderColor: selected ? frameColor : "rgba(255,255,255,0.1)",
                boxShadow: selected ? `0 0 10px ${frameColor}66` : "none",
              }}
            >
              <img
                src={avatarUrl(id)}
                alt={id}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </button>
          );
        })}
      </div>

      <div className="flex justify-end gap-2">
        <Button
          onClick={save}
          disabled={saving}
          className="bg-emerald-600 hover:bg-emerald-500"
        >
          {saving ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          Save appearance
        </Button>
      </div>

      <p className="text-[11px] text-zinc-500 flex items-center gap-1">
        <UserCircle className="h-3.5 w-3.5" />
        CraftPix packs: cute · cartoon · funny (105 avatars). Stored on your
        poker account (D1). Applies next time you sit at a table.
      </p>
    </div>
  );
}
